/*
 * Poly2Tri Copyright (c) 2009-2014, Poly2Tri Contributors
 * http://code.google.com/p/poly2tri/
 *
 * poly2tri.js (JavaScript port) (c) 2009-2014, Poly2Tri Contributors
 * https://github.com/r3mi/poly2tri.js
 *
 * All rights reserved.
 *
 * Distributed under the 3-clause BSD License, see LICENSE.txt
 */

/**
 * poly2tri.js supports using custom point class instead of {@linkcode Point}.
 * Any "Point like" object with <code>{x, y}</code> attributes is supported
 * to initialize the SweepContext polylines and points
 * ([duck typing]{@link http://en.wikipedia.org/wiki/Duck_typing}).
 *
 * poly2tri.js might add extra fields to the point objects when computing the
 * triangulation : they are prefixed with <code>_p2t_</code> to avoid collisions
 * with fields in the custom class.
 */
export interface XY {
	x: number
	y: number
	// Optional poly2tri internal fields
	_p2t_edge_list?: any[]
}

/**
 * Point pretty printing : prints x and y coordinates.
 */
export function toStringBase(p: XY): string {
	return `(${p.x};${p.y})`
}

/**
 * Point pretty printing. Delegates to the point's custom "toString()" method if exists,
 * else simply prints x and y coordinates.
 */
export function toString(p: XY): string {
	// Try a custom toString first, and fallback to own implementation if none
	const s = (p as any).toString?.()
	return s === '[object Object]' ? toStringBase(p) : s
}

/**
 * Compare two points component-wise. Ordered by y axis first, then x axis.
 */
export function compare(a: XY, b: XY): number {
	if (a.y === b.y) {
		return a.x - b.x
	} else {
		return a.y - b.y
	}
}

/**
 * Test two Point objects for equality.
 */
export function equals(a: XY, b: XY): boolean {
	return a.x === b.x && a.y === b.y
}

